import {
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AppContext } from "../context";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useDailyEvent } from "@daily-co/daily-react-hooks";
// import Call from "../components/daily-components/Call/Call";
// import Tray from "../components/daily-components/Tray/Tray";
import {
  STATE_JOINED,
  useDailyEvents,
} from "../services/daily-utils/useDailyEvents";
import { DAILY, VIDEOSDK } from "../constants";
// import Chat from "../components/daily-components/Chat/Chat";
import {
  StartMeetingRecording,
  StopMeetingRecording,
} from "../services/cohortService";
import { useCountries } from "../hooks/useCountries";
import { DailyProvider, useCallFrame } from "@daily-co/daily-react";
import notification from "antd/lib/notification";
import { useSentry } from "../hooks/useSentry";
import list from "../assets/list-white.svg";
import codeIcon from "../assets/code.svg";
import notes from "../assets/pen-white.svg";
import { Drawer } from "antd";
import WhiteboardView from "../components/CallComponents/WhiteBoard/WhiteBoard";
// import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/components/prism-javascript"; // Replace with your language if needed
import "prismjs/themes/prism-tomorrow.css";
import { updateUserSlice } from "../store/slices/userSlice";
import { GetUser } from "../services/NewAuthService";
import ErrorBoundary from "../pages/ErrorBoundary";
import { useCookies } from "react-cookie";
import { qalort_token } from "../components/menu/constant";

export const provider = DAILY;
const Wrapper = ({ children }) => {
  const Editor = lazy(() => import("react-simple-code-editor"));
  const countries = useCountries(); // do not delete!
  const [cookies, setCookie] = useCookies([qalort_token]);
  const { meetingPayload, setMeetingPayload } = useContext(AppContext);
  const callRef = useRef(null);
  const callFrame = useCallFrame({
    parentElRef: callRef,
    options: {
      iframeStyle: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
      },
      showUserNameChangeUI: true,
      showLeaveButton: true,
      showParticipantsBar: true,
      activeSpeakerMode: true,
      dailyConfig: {
        useDevicePreferenceCookies: false,
      },
    },
  });

  const { openSide, meetingInfo, showCall, callObject } = meetingPayload;
  const location = useLocation();
  const dispatch = useDispatch();
  const qalortUser = useSelector((state) => state.user.userData);
  const appSentry = useSentry();
  const { startLeavingCall, appState } = useDailyEvents();
  const [showNotes, setShowNotes] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showWhiteBoard, setUseWhiteBoard] = useState(false);
  const [showCodeEditor, setShowCodeEditor] = useState(false);
  /* If a participant runs into a getUserMedia() error, we need to warn them. */
  const [getUserMediaError, setGetUserMediaError] = useState(false);
  const [newChatMessage, setNewChatMessage] = useState(false);
  const [code, setCode] = useState(`function add(a, b) {\n  return a + b;\n}`);

  /* We can use the useDailyEvent() hook to listen for daily-js events. Here's a full list
   * of all events: https://docs.daily.co/reference/daily-js/events */
  useDailyEvent(
    "camera-error",
    useCallback(() => {
      setGetUserMediaError(true);
    }, [])
  );

  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    appSentry.init();
    const cookie_token = cookies.qalort_token;

    if (cookie_token) {
      getMe(cookie_token);
      localStorage.setItem(qalort_token, cookie_token);
    }
    //leave any open meetings on app close
    return () => {
      appState === STATE_JOINED && startLeavingCall();
    };
  }, []);

  useEffect(() => {
    if (showWhiteBoard) {
      setShowNotes(false);
      setShowCodeEditor(false);
    }

    if (showNotes) {
      setUseWhiteBoard(false);
      setShowCodeEditor(false);
    }

    if (showCodeEditor) {
      setShowNotes(false);
      setUseWhiteBoard(false);
    }
  }, [showWhiteBoard, showNotes, showChat, showCodeEditor]);

  useEffect(() => {
    if (callFrame) {
      setMeetingPayload({
        ...meetingPayload,
        callFrame,
      });
    }
  }, [callFrame, callRef]);

  useEffect(() => {
    if (callFrame && !callFrame.isDestroyed) {
      callFrame?.setTheme({
        colors: {
          accent: "#5f5ffa",
          background: "#e8e8f1",
          accentText: "#ffffff",
          mainAreaBg: "#060611",
        },
      });

      callFrame?.on("left-meeting", () => {
        setMeetingPayload({ ...meetingPayload, showCall: false });
        callFrame?.destroy();
      });

      callFrame?.on("recording-started", (e) => {
        handleRecording(e?.recordingId);
      });

      callFrame?.on("recording-stopped", (e) => {
        stopRecording();
      });
    }
  }, [callFrame, meetingPayload]);

  const handleRecording = async (recording_id) => {
    const roomID =
      meetingPayload?.meetingInfo?.meeting_link.split("daily.co/")[1];
    const data = {
      room_id: roomID,
      recording_id,
    };
    if (
      meetingPayload?.meetingInfo?.schedules &&
      meetingPayload?.meetingInfo?.schedules.length
    ) {
      data.cohort_id = meetingPayload?.meetingInfo?.id;
    } else {
      data.appointment_id = meetingPayload?.meetingInfo?.id;
    }

    if (!recording_id || !data.room_id) {
      return;
    }

    try {
      const res = await StartMeetingRecording(data);
      setMeetingPayload({
        ...meetingPayload,
        recordingId: recording_id,
      });
    } catch (error) {
      console.error(error);
      if (error?.response?.status !== 500) {
        notification.error({
          message: error.response?.data?.message || "An error occured",
        });
      }
    }
  };

  const stopRecording = async () => {
    const roomID =
      meetingPayload?.meetingInfo?.meeting_link.split("daily.co/")[1];
    try {
      await StopMeetingRecording({
        recording_id: meetingPayload.recordingId,
        room_id: roomID,
      });

      notification.success({
        message: "Meeting recording has been stopped",
      });
    } catch (error) {}
  };

  // Floating button state
  const [showSmallButtons, setShowSmallButtons] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [drawerType, setDrawerType] = useState(null);

  // Toggle small buttons
  const toggleSmallButtons = () => {
    setShowSmallButtons((prev) => !prev);
  };

  const openDrawerHandler = (type) => {
    setOpenDrawer(true);
    setDrawerType(type);
  };

  const getMe = (token) => {
    const req = GetUser(token);
    req.then((res) => {
      let data = res.data;
      const payload = {
        ...qalortUser,
        ...data.data,
        full_name: `${data?.data?.first_name} ${data?.data?.last_name}`,
        token,
      };
      dispatch(updateUserSlice(payload));
    });
  };

  return (
    <ErrorBoundary>
      {children}

      {/* <div className="flex relative z-99">
        <div
          className={
            showCall
              ? "w-full h-screen absolute top-0 left-0 z-[1000]"
              : "w-0 h-0 hidden"
          }
        >
          <DailyProvider callObject={callFrame}>
            <div ref={callRef} />
          </DailyProvider>
        </div>

        <div>
          <Drawer
            title={drawerType === "code" ? "Code Editor" : "White Board"}
            placement="right"
            onClose={() => setOpenDrawer(false)}
            open={openDrawer}
          >
            <div className="w-full md:w-[900px]">
              {drawerType === "code" ? (
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center p-10">
                      Loading...
                    </div>
                  }
                >
                  <Editor
                    value={code}
                    onValueChange={(code) => setCode(code)}
                    highlight={(code) =>
                      Prism.highlight(
                        code,
                        Prism.languages.javascript,
                        "javascript"
                      )
                    }
                    padding={10}
                    style={{
                      fontFamily: '"Fira Code", "Fira Mono", monospace',
                      fontSize: 14,
                      backgroundColor: "#1e1e1e", // VSCode dark theme background
                      color: "#d4d4d4", // VSCode light gray text
                      minHeight: "600px",
                      borderRadius: "5px",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
                      overflow: "auto",
                    }}
                  />
                </Suspense>
              ) : (
                <WhiteboardView />
              )}
            </div>
          </Drawer>
        </div>
      </div> */}

      {/* Floating Button */}
      {!openDrawer && showCall && (
        <div className="fixed bottom-[100px] right-2 z-[99999]">
          <button
            onClick={toggleSmallButtons}
            className="bg-[#ffffff] text-white w-[50px] h-[50px] flex items-center justify-center rounded-full shadow-lg"
          >
            <img src={list} alt="list icon" className="" />
          </button>

          {showSmallButtons && (
            <div className="flex flex-col items-center space-y-2 mt-2">
              <button
                onClick={() => openDrawerHandler("code")}
                className="bg-green-500 text-white w-[40px] h-[40px] flex items-center justify-center rounded-full shadow"
              >
                <img
                  src={codeIcon}
                  alt=""
                  className="filter invert brightness-200 contrast-200"
                />
              </button>
              <button
                onClick={() => openDrawerHandler("board")}
                className="bg-[#e61a1a] text-white w-[40px] h-[40px] flex items-center justify-center rounded-full shadow"
              >
                <img
                  src={notes}
                  alt=""
                  className="filter invert brightness-200 contrast-200"
                />
              </button>
            </div>
          )}
        </div>
      )}
    </ErrorBoundary>
  );
};

export default Wrapper;
